import { Divider, Flex } from 'antd'
import { FunctionComponent } from 'react'
import styled from 'styled-components'
import KolPlatformLink from '@/components/kol/common/kol-platform-link'
import AudienceGenderStatistic from '@/components/kol/common/statistics/audience-gender-statistic'
import AudienceMainAgeStatistic from '@/components/kol/common/statistics/audience-main-age-statistic'
import EngagementRateStatistic from '@/components/kol/common/statistics/engagement-rate-statistic'
import FansUpRateStatistic from '@/components/kol/common/statistics/fans-up-rate-statistic'
import FollowerStatistic from '@/components/kol/common/statistics/follower-statistic'
import KScore from '@/components/kol/common/statistics/k-score'
import { PostQuotationStatistic } from '@/components/kol/common/statistics/post-quotation-statistic'
import { VideoQuotationStatistic } from '@/components/kol/common/statistics/video-quotation-statistic'
import ViewRateStatistic from '@/components/kol/common/statistics/view-rate-statistic'
import UnlockButton from '@/components/kol/common/unlock-button'
import { statisticSkeletonProps } from '@/components/kol/detail/overview/statistics'
import useIsQuotaBasedWorkspace from '@/hooks/use-is-quota-based-workspace'
import { useShouldDisplayQuotations } from '@/hooks/use-should-display-quotations'
import useWorkspaceCurrency from '@/hooks/use-workspace-currency'
import { useIntl } from '@/i18n/hooks/use-intl'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { VisitKolSocialMediaProperties } from '@/utils/ampli'
import {
  convertPlatformShortcode,
  mainSupportedPlatforms,
  PlatformShortcode,
} from '@/utils/convert-platform'

interface StatisticCardProps {
  isKolInfoLocked: boolean
  isFetching: boolean
  kolInfo?: KolSchemaModel
  platformShortCode?: PlatformShortcode
  from?: VisitKolSocialMediaProperties['from']
}

const StatisticCard: FunctionComponent<StatisticCardProps> = ({
  kolInfo,
  platformShortCode,
  isFetching,
  isKolInfoLocked,
  from,
}) => {
  const { formatMessage } = useIntl()

  const isQuotaBasedWorkspace = useIsQuotaBasedWorkspace()
  const workspaceCurrency = useWorkspaceCurrency()
  const shouldDisplayQuotations = useShouldDisplayQuotations()

  const kpi = kolInfo?.kpi.find((kpi) => kpi.platform === platformShortCode)
  const loading = isFetching && statisticSkeletonProps
  const platform = convertPlatformShortcode(platformShortCode)
  const showMainPlatformStatistic = mainSupportedPlatforms.some(
    ({ shortcode }) => shortcode === platformShortCode,
  )
  const displayQuotations = shouldDisplayQuotations(platformShortCode)

  return (
    <Wrapper>
      <KolPlatformLink
        isClickedFromDrawer
        from={from}
        isFetching={isFetching}
        kolInfo={kolInfo}
        platformShortcode={platformShortCode}
      />
      <KScore
        isKolInfoLocked={isKolInfoLocked}
        kolInfo={kolInfo}
        platformShortcode={platformShortCode}
      />
      <StatisticsWrapper>
        <FollowerStatistic followerCount={kpi?.follower} loading={loading} />
        <EngagementRateStatistic
          isKolInfoLocked={isKolInfoLocked}
          kpi={kpi}
          loading={loading}
        />
        <ViewRateStatistic
          isKolInfoLocked={isKolInfoLocked}
          kpi={kpi}
          loading={loading}
          platformShortCode={platformShortCode}
        />
        <FansUpRateStatistic
          isKolInfoLocked={isKolInfoLocked}
          kpi={kpi}
          loading={loading}
        />
        {showMainPlatformStatistic && (
          <>
            <AudienceGenderStatistic
              isKolInfoLocked={isKolInfoLocked}
              kpi={kpi}
              statisticLoading={loading}
            />
            <AudienceMainAgeStatistic
              isFetchingKolInfo={isFetching}
              isKolInfoLocked={isKolInfoLocked}
              kolInfo={kolInfo}
              platformCode={platform.code}
              statisticLoading={loading}
            />
          </>
        )}
      </StatisticsWrapper>
      {displayQuotations && (
        <>
          <Divider />
          <Flex vertical gap={24}>
            <Flex gap={24} justify='space-between'>
              <SectionHeader>
                {formatMessage({
                  id: 'kol:collaboration_value_estimation_data_block_title_without_platform',
                })}
              </SectionHeader>
              <Currency>
                {formatMessage(
                  { id: 'general:displaying_currency' },
                  {
                    currencyCode: workspaceCurrency,
                  },
                )}
              </Currency>
            </Flex>
            <Flex gap={20}>
              <PostQuotationStatistic
                currencyCode={workspaceCurrency}
                isKolInfoLocked={isKolInfoLocked}
                loading={!!loading}
                quotationPriceRange={kpi?.quotations?.post}
              />
              <VideoQuotationStatistic
                currencyCode={workspaceCurrency}
                isKolInfoLocked={isKolInfoLocked}
                loading={!!loading}
                quotationPriceRange={kpi?.quotations?.video}
              />
            </Flex>
          </Flex>
        </>
      )}
      {!isFetching && isKolInfoLocked && isQuotaBasedWorkspace && (
        <UnlockButtonBox>
          <div>
            {formatMessage({ id: 'kol:unlock_kol_detail_description' })}
          </div>
          <UnlockButton
            isClickedFromDrawer
            buttonI18nId='general:unlock_act'
            from={from}
            kolInfo={kolInfo}
          />
        </UnlockButtonBox>
      )}
    </Wrapper>
  )
}

const SectionHeader = styled.div`
  color: ${({ theme }): string => theme.colors.text.secondary};
  font-size: 16px;
  font-weight: 700;
  line-height: 140%; /* 22.4px */
`

const Currency = styled.div`
  color: ${({ theme }): string => theme.colors.gray7};
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`

export default StatisticCard

const UnlockButtonBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }): string => theme.colors.text.primary};
  width: 100%;
  border-radius: 4px;
  background: ${({ theme }): string => theme.colors.background.green};
  margin-top: 30px;
  padding: 12px 24px;
`

const StatisticsWrapper = styled.div`
  display: grid;
  gap: 40px 20px;
  grid-template-columns: repeat(3, 1fr);
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  background: ${({ theme }): string => theme.colors.background.white};
  border-radius: 4px;
  padding: 24px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
`
